import React, { useContext } from 'react'
import { Link as ChakraLink } from '@chakra-ui/react'
import { forwardRef, HubLinkContext } from '@hub/design-system-base'
import type { As, HubStyleObject } from '@hub/design-system-base'
import { Button } from '@hub/button'
import type { ButtonProps } from '@hub/button'
import { Box } from '@hub/box'
import { LinkOverlay } from './link-overlay'

interface LinkProps {
  as?: As
  sx?: HubStyleObject
  href: string
  isExternal?: boolean
  hasUnderline?: boolean
  className?: string
  onClick?: React.MouseEventHandler
}

// TODO: Make work with Next links, external links, and internal non-Next links
export const Link = forwardRef<LinkProps, typeof ChakraLink>(
  (
    {
      children,
      href,
      isExternal,
      as,
      sx,
      hasUnderline = false,
      className,
      onClick,
    },
    ref
  ) => {
    const Link = useContext(HubLinkContext)

    return (
      <ChakraLink
        ref={ref}
        href={href}
        as={
          as ||
          (Link &&
            (props => <Link as={props.href} to={props.href} {...props} />))
        }
        sx={sx}
        isExternal={isExternal}
        className={className}
        onClick={onClick}
        {...(hasUnderline ? { textDecoration: 'underline' } : {})}
      >
        {children ?? href}
      </ChakraLink>
    )
  }
)

export const LinkButton: React.FC<
  LinkProps & Omit<ButtonProps, 'onClick'>
> = props => (
  // We want an `<a>` tag that inherits all the logic of a `<Link>`, which looks
  // and behaves like a `<Button>`.
  // To do that, we need to combine a couple of components which gets us to the
  // right combination of things that are rendered.
  // (1) We render a `<Link>` component which adds in logic for `rel`, etc
  // (2) We hijack the rendered component from `<Link>` and instead render a
  // `<Button>` which give the desired styles & behaviour
  // (3) But we spread all the props from the `<Link>` onto the `<Button>` to
  // ensure anything useful gets passed in (such as `children`, `sx`, etc)
  // (4) But, `<Button>` tries to render a `<button>` tag, which we want to
  // override with an `<a>` tag
  // (5) Finally, we ensure that all the right props are passed to the thing
  // that's actually rendered
  <Link // (1)
    {...props}
    as={linkProps => (
      <Button // (2)
        {...linkProps} // (3)
        as={buttonProps => (
          <Box
            as={Link ? props => <Link to={props.href} {...props} /> : 'a'} // (4)
            {...linkProps} // (5)
            {...buttonProps}
          />
        )}
      />
    )}
  />
)

export const LinkButtonOverlay: React.FC<
  LinkProps & Omit<ButtonProps, 'onClick'>
> = props => (
  // Very similar to <LinkButton>, but we manually combine the classNames which
  // is safe to do because `<LinkOverlay>` is unstyled by default in Chakra.
  <LinkOverlay
    {...props}
    as={linkProps => (
      <Button
        {...props}
        {...linkProps}
        as={buttonProps => (
          <Box
            as={Link ? props => <Link to={props.href} {...props} /> : 'a'}
            {...buttonProps}
            {...linkProps}
            className={`${buttonProps.className} ${linkProps.className}`}
          />
        )}
      />
    )}
  />
)

export { LinkBox } from './link-box'
export { LinkOverlay }
